<template>
  <OurPage class="access-groups" :title="$t('title.knowledgeBase.chapters')" width="md">
    <template #header-right>
      <OurLink v-if="!isUserDealer && getPermission['knowledge_base.create']" :route="addRoute">
        <OurButton :text="$t('button.add')" />
      </OurLink>
    </template>

    <DeleteSectionModal
      :show.sync="isShownDeleteSectionModal"
      :knowledge-base-sections-id="sectionId"
      :knowledge-base-sections-name="sectionName"
      @deleteKnowledgeBaseSection="getData"
    />

    <DeleteArticleModal
      :show.sync="isShownDeleteArticleModal"
      :article-id="articleId"
      :article-name="articleName"
      @deleteArticle="getData"
    />

    <OurTableList
      :have-permission-delete="getPermission['knowledge_base.delete']"
      :have-permission-edit="getPermission['knowledge_base.update']"
      :list="items"
      @clickEdit="onClickEdit"
      @clickDelete="onClickDelete"
      @clickChapter="onClickItem"
      @dragSort="dragHandler"
    />
  </OurPage>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import apolloMixin from "@/mixins/apollo.mixin";
import DeleteSectionModal from "@/views/settings/base-knowledge/delete/Modal.vue";
import DeleteArticleModal from "@/views/settings/base-knowledge/chapter/delete/Modal.vue";
import { reorderKnowledgeBaseItemsMutation } from "@/views/settings/base-knowledge/index/gql/mutations";
import {
  isKnowledgeBaseSection,
  toKnowledgeBaseItemRefs,
} from "@/views/settings/base-knowledge/services/items";

export default {
  name: "BaseKnowledge",
  components: { DeleteSectionModal, DeleteArticleModal },

  mixins: [apolloMixin],

  data() {
    return {
      isShownDeleteSectionModal: false,
      isShownDeleteArticleModal: false,
      sectionId: "",
      sectionName: "",
      articleId: "",
      articleName: "",
    };
  },

  computed: {
    ...mapGetters("user", ["isUserDealer", "getPermission"]),
    ...mapGetters("knowledgeBase", {
      items: "getKnowledgeBaseItems",
    }),

    addRoute: () => ({ name: "BaseKnowledgeAdd" }),
  },

  created() {
    this.getData();
  },

  methods: {
    ...mapMutations("knowledgeBase", ["SET_KNOWLEDGE_BASE_ITEMS"]),

    isSection: isKnowledgeBaseSection,

    findItem(id) {
      return this.items.find((item) => String(item.id) === String(id));
    },

    getData() {
      return this.$store.dispatch("knowledgeBase/fetchKnowledgeBaseItems");
    },

    onClickItem(id) {
      const item = this.findItem(id);

      if (!item) {
        return;
      }

      if (this.isSection(item)) {
        this.$router.push({ name: "Chapter", params: { id: item.id } });

        return;
      }

      this.onClickEdit(item.id);
    },

    onClickEdit(id) {
      const item = this.findItem(id);

      if (!item) {
        return;
      }

      if (this.isSection(item)) {
        this.$router.push({ name: "BaseKnowledgeEdit", params: { id: item.id } });

        return;
      }

      if (!item.parentId) {
        return;
      }

      this.$router.push({
        name: "ChapterEdit",
        params: { id: item.parentId, articleId: item.id },
      });
    },

    onClickDelete(id) {
      const item = this.findItem(id);

      if (!item) {
        return;
      }

      if (this.isSection(item)) {
        this.isShownDeleteSectionModal = true;
        this.sectionId = item.id;
        this.sectionName = item.name;

        return;
      }

      this.isShownDeleteArticleModal = true;
      this.articleId = item.id;
      this.articleName = item.name;
    },

    async dragHandler() {
      const order = toKnowledgeBaseItemRefs(this.items);

      this.applyRootItemsOrder();

      await this.$post(
        reorderKnowledgeBaseItemsMutation,
        { order, parentId: null },
        {
          delaySuccessNotify: false,
        }
      );
    },

    applyRootItemsOrder() {
      const currentItems = this.$store.state.knowledgeBase.knowledgeBaseItems;
      const restItems = currentItems.filter((item) => item.parentId);

      this.SET_KNOWLEDGE_BASE_ITEMS([...this.items, ...restItems]);
    },
  },
};
</script>
